import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Col, Form, Row } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import debounce from "lodash/debounce";
import omit from "lodash/omit";
import { useAppDispatch, useAppSelector } from "../..";
import type { DestinationListSearchProps } from "../../store/slices/destinations/types";
import {
  fetchDestinationList,
  fetchDestinationSets,
  fetchAccountsByName,
  cleanAccountSelect,
  cleanDestinationList,
  fetchDestinationVersions,
  cleanDestinationVersions,
  setDestinationListFilter,
  resetDestinationListFilter,
  initialDestinationListFilter,
} from "../../store/slices/destinations/destinations";
import {
  fetchDestinationTypes,
  fetchDestinationCategories,
  fetchCountries,
  fetchRegions,
} from "../../store/slices/dicts";
import { checkNotLoaded } from "../../lib/utils";
import { DestinationsListSearch, DestinationList } from "../../components/DestinationList";
import { hasAccess } from "../../components/RolesModule/hasAccess";
import { MENU_TC_ADMIN_DESTINATIONS_WRITE } from "../../lib/const/permissions";

// Redux не воспринимает объекты Moment, а DatePicker ожидает Moment, поэтому приходится преобразовывать
const normalizeDate = (props: Partial<DestinationListSearchProps>, consumer: "state" | "form") => {
  if (!props.onDate) return props;
  const onDate = consumer === "state" ? moment(props.onDate).format() : moment(props.onDate);
  return { ...props, onDate };
};

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const DestinationListContainer = () => {
  const dispatch = useAppDispatch();
  const {
    countriesLoading,
    destinationTypesLoading,
    destinationCategoriesLoading,
    regionsLoading,
  } = useAppSelector((state) => state.dicts);
  const { destinationSetsLoading, destinationListFilter } = useAppSelector(
    (state) => state.destinations
  );
  const [searchForm] = Form.useForm<DestinationListSearchProps>();

  const [isLoading, setIsLoading] = useState(false);

  // Загрузка справочников
  useEffect(() => {
    if (checkNotLoaded(destinationSetsLoading)) dispatch(fetchDestinationSets());
    if (checkNotLoaded(destinationTypesLoading)) dispatch(fetchDestinationTypes());
    if (checkNotLoaded(destinationCategoriesLoading)) dispatch(fetchDestinationCategories());
    if (checkNotLoaded(countriesLoading)) dispatch(fetchCountries());
    if (checkNotLoaded(regionsLoading)) dispatch(fetchRegions());
  }, []);

  // Сохранение в стейт значений фильтра поиска
  const setDestinationListFilterState = (props: Partial<DestinationListSearchProps>) => {
    const values = {
      ...destinationListFilter,
      ...props,
    };
    dispatch(setDestinationListFilter(normalizeDate(values, "state")));
  };

  // Запись в searchForm поисковых значений
  const setDestinationListFilterForm = (props: Partial<DestinationListSearchProps>) => {
    searchForm.setFieldsValue(normalizeDate(props, "form"));
  };

  const onSearch = () => {
    const { onDate, destinationSetId, accountId } = destinationListFilter;
    if (!onDate) return;
    if (!destinationSetId && !accountId) return;
    setIsLoading(true);
    const searchValues: DestinationListSearchProps = {
      ...omit(destinationListFilter, "filterOpen"),
      page: 0,
    };
    dispatch(fetchDestinationList(searchValues)).finally(() => {
      setDestinationListFilterState({ page: 1 });
      setIsLoading(false);
    });
  };

  const onSearchPageOrSize = (params?: { page: number }) => {
    const { onDate, destinationSetId, accountId } = destinationListFilter;
    if (!onDate) return;
    if (!destinationSetId && !accountId) return;
    setIsLoading(true);
    const searchValues: DestinationListSearchProps = {
      ...omit(destinationListFilter, "filterOpen"),
      page: params ? params.page - 1 : destinationListFilter.page - 1,
    };
    dispatch(fetchDestinationList(searchValues)).finally(() => {
      setIsLoading(false);
    });
  };

  // Запись в форму поиска значений из стейта при входе
  useEffect(() => {
    setDestinationListFilterForm(destinationListFilter);
  }, []);

  // Поиск дефолтный (при наличии значений) и при изменении пагинации или размера страницы (кол-во строк)
  useEffect(() => {
    onSearchPageOrSize();
  }, [destinationListFilter.size]);

  const onResetSearch = (): void => {
    dispatch(cleanDestinationList());
    dispatch(cleanAccountSelect());
    dispatch(resetDestinationListFilter());
    setDestinationListFilterForm(initialDestinationListFilter);
  };

  const cleanSearchedAccounts = () => {
    dispatch(cleanAccountSelect());
  };

  const searchAccountsByName = debounce((name: string): void => {
    if (name) {
      dispatch(fetchAccountsByName({ name }));
    } else {
      cleanSearchedAccounts();
    }
  }, 500);

  const onOpenVersions = (destinationId: number) => {
    dispatch(fetchDestinationVersions(destinationId));
  };

  const onCloseVersions = () => {
    dispatch(cleanDestinationVersions());
  };

  return (
    <div className="container_height">
      <h1 className="page-title">Направления</h1>
      {hasAccess(MENU_TC_ADMIN_DESTINATIONS_WRITE) && (
        <Row justify="end" style={{ marginBottom: 15, paddingRight: 10 }}>
          <Col>
            <Link to="/destination/create">
              <Button type="primary" icon={<PlusOutlined />}>
                Создать направление
              </Button>
            </Link>
          </Col>
        </Row>
      )}
      <DestinationsListSearch
        searchForm={searchForm}
        onSearch={onSearch}
        onResetSearch={onResetSearch}
        searchAccountsByName={searchAccountsByName}
        cleanSearchedAccounts={cleanSearchedAccounts}
        isLoading={isLoading}
        setDestinationListFilterState={setDestinationListFilterState}
      />
      <DestinationList
        onOpenVersions={onOpenVersions}
        onCloseVersions={onCloseVersions}
        setPage={(page: number) => {
          setDestinationListFilterState({ page });
          onSearchPageOrSize({ page });
        }}
        setSize={(size: number) => {
          setDestinationListFilterState({ size });
        }}
      />
    </div>
  );
};
